import { buildVersion } from "js/config";
import { slideTemplates } from "../../../core/slide_templates/slide_template";
import { _ } from "js/vendor";
import { ds } from "js/core/models/dataService";
import { Slide } from "../../../core/models/slide";
import log from "js/core/logger";
import Api from "js/core/api";

export class BaseSlideLibraryDataService {
    loadData() {
        this.data = [];
    }

    getItems(search, appSearchResults, sort) {
        let data;

        if (search) {
            data = this.search(search, appSearchResults);
        } else {
            data = this.getAllDataAsArray();
        }

        //currently as per requirements when there is a search param we will ignore the sort
        if (!search && sort) {
            let sortedData;
            sortedData = _.sortBy(data, o => {
                if (typeof o[sort.field] == "string") {
                    return o[sort.field].toLowerCase();
                } else {
                    return o[sort.field];
                }
            });

            if (sort.reverse) {
                sortedData = sortedData.reverse();
            }
            return sortedData;
        }

        return data;
    }

    getAllDataAsArray() {
        return _.map(this.data, (v, k) => {
            return { id: k, ...v };
        });
    }

    search(query, appSearchResults) {
        let results = this.performSearch(query, appSearchResults);

        let items;
        if (results) {
            items = results.map(result => this.data[result.ref]).filter(x => x);
        } else {
            items = this.getAllDataAsArray();
        }
        return items;
    }

    performSearch(query, appSearchResults) {
        query = query.toLowerCase().trim();
        let results;
        if (query.length === 0) {
            results = null;
        } else {
            results = appSearchResults.map(searchResult => {
                return { ref: searchResult.id.raw };
            });
        }

        return results;
    }
}

export class SlideTemplateDataService extends BaseSlideLibraryDataService {
    async loadData() {
        this.data = slideTemplates;
    }

    search(query, appSearchResults) {
        let results = this.performSearch(query, appSearchResults);

        let items;
        if (results) {
            items = results;
        } else {
            items = this.getAllDataAsArray();
        }
        return items;
    }

    performSearch(query, appSearchResults) {
        query = query.toLowerCase().trim();
        let results;
        if (query.length === 0) {
            results = null;
        } else {
            const appSearchTemplateTitles = appSearchResults.reduce((acc, cur) => {
                acc[cur.id.raw] = true;
                return acc;
            }, {});

            results = Object.keys(appSearchTemplateTitles).map(searchTemplateTitle => {
                return this.data[searchTemplateTitle];
            }).filter(item => {
                return item !== undefined;
            });
        }
        //hidden templates should not be displayed as a search result
        return results.filter(result => !result.hideTemplate);
    }
}

export class SlideGalleryDataService extends BaseSlideLibraryDataService {
    async loadData() {
        if (!this.loadDataPromise) {
            this.loadDataPromise = (async () => {
                // buildVersion is used for cache busting when the app is updated
                const slides = await Api.gallerySlidesMetadata.get({ buildVersion });
                // Enrich slides with the necessary data
                slides.forEach(slide => {
                    slide.template = slide.template_id;
                    slide.title = slideTemplates[slide.template].title;
                    slide.description = slideTemplates[slide.template].description;
                    slide.tags = slideTemplates[slide.template].tags;
                });
                this.data = _.keyBy(slides, record => record.id);
            })();
        }

        await this.loadDataPromise;
    }

    getExactItems(search, appSearchResults, templateId) {
        let data;

        if (search) {
            data = this.search(search, appSearchResults);
        } else {
            data = this.getAllDataAsArray();
        }

        return data.filter(item => item && item.template === templateId);
    }

    getSimilarItems(search, appSearchResults, templateId) {
        let data;

        if (search) {
            data = this.search(search, appSearchResults);
        } else {
            data = this.getAllDataAsArray();
        }

        return data.filter(item => item && item.template !== templateId);
    }
}

export class TeamSlideDataService extends BaseSlideLibraryDataService {
    static CreateLibraryItemObjectFromModel(model) {
        const enrichedItem = _.cloneDeep(model.attributes);
        enrichedItem.libraryItem = model;
        enrichedItem.tags = Object.keys(model.attributes.tags || {});
        enrichedItem.presentationCount = Object.keys(model.attributes.usedInPresentations || {}).length;
        enrichedItem.ratedByCount = Object.keys(model.attributes.ratedByUsers || {}).length;
        enrichedItem.ratedByUsers = model.ratedByUsers;
        enrichedItem.createdAt = model.attributes.createdAt;
        enrichedItem.modifiedAt = model.attributes.modifiedAt;
        enrichedItem.id = model.attributes.contentId;
        enrichedItem.libraryId = model.attributes.id;
        return enrichedItem;
    }

    constructor(config) {
        super(config);
        this.config = config;

        ds.teams.on("change", this.onChange);
    }

    onChange() {
        this.config?.onChanged();
    }

    async loadData(organizationId, includeDisabledSlides) {
        if (!organizationId) {
            throw new Error("OrganizationId is required");
        }

        let presentationId;
        let firstSlideModifiedAt;

        if (ds.selection.presentation) {
            presentationId = ds.selection.presentation.id;
            firstSlideModifiedAt = ds.selection.presentation.get("firstSlideModifiedAt");
        }

        let defaultTeam = ds.teams.defaultTeamForOrg(organizationId);

        const libraryItems = defaultTeam.libraryItems.filter(libraryItem => libraryItem.get("type") === "slides");
        const data = libraryItems.map(libraryItem => TeamSlideDataService.CreateLibraryItemObjectFromModel(libraryItem));

        //filter out slides that are disabled
        let filteredData;

        if (includeDisabledSlides) {
            filteredData = data;
        } else {
            filteredData = data.filter(lib => !lib.isDisabled);
        }
        this.loadTags(filteredData);

        this.data = _.keyBy(filteredData, d => d.id);
    }

    loadTags(data) {
        let items = [];
        this.tags = [];
        const tagCountMap = {};

        data.forEach(item => {
            if (item.tags) {
                items = items.concat(item.tags);
            }
        });

        items.forEach(item => {
            if (!tagCountMap[item]) {
                tagCountMap[item] = 0;
            }
            tagCountMap[item]++;
        });

        const uniqueTags = new Set(items);

        uniqueTags.forEach(tag => {
            this.tags.push({ id: tag, name: `${tag} (${tagCountMap[tag]})` });
        });
    }

    getItems(search, appSearchResults, sort, tag) {
        let data;

        if (search) {
            data = this.search(search, appSearchResults);
        } else {
            data = this.getAllDataAsArray();
        }

        if (tag && tag != "all_tags") {
            data = _.filter(data, item => item.tags.includes(tag));
        }

        //currently as per requirements when there is a search param we will ignore the sort
        if (!search && sort) {
            let sortedData;
            sortedData = _.sortBy(data, o => {
                if (typeof o[sort.field] == "string") {
                    return o[sort.field].toLowerCase();
                } else {
                    return o[sort.field];
                }
            });

            if (sort.reverse) {
                sortedData = sortedData.reverse();
            }
            return sortedData;
        }

        return data;
    }
}

export class CopySlideDataService extends BaseSlideLibraryDataService {
    async loadData(organizationId) {
        const filteredPresentations = ds.presentations
            .map(presentation => presentation.attributes)
            .filter(presentation => presentation.orgId === organizationId);

        this.data = _.keyBy(filteredPresentations, d => d.id);
    }

    getItems(search, appSearchResults, sort, items) {
        let data;

        if (search) {
            data = this.search(search, appSearchResults);
        } else {
            data = items;
        }

        //currently as per requirements when there is a search param we will ignore the sort
        if (!search && sort) {
            let sortedData;
            sortedData = _.sortBy(data, o => {
                if (typeof o[sort.field] == "string") {
                    return o[sort.field].toLowerCase();
                } else {
                    return o[sort.field];
                }
            });

            if (sort.reverse) {
                sortedData = sortedData.reverse();
            }
            return sortedData;
        }

        return data;
    }

    search(query, appSearchResults) {
        let results = this.performSearch(query, appSearchResults);

        let items = [];
        if (results) {
            items = [...results];
        } else {
            items = this.getAllDataAsArray();
        }
        return items;
    }

    performSearch(query, appSearchResults) {
        query = query.toLowerCase().trim();
        let results;
        if (query.length === 0) {
            results = null;
        } else {
            results = appSearchResults.map(result => {
                const presentation = ds.presentations.get(result.presentation_id.raw);

                if (!presentation) {
                    return;
                }
                const flippedSlideSips = _.invert(presentation.getSips());

                return {
                    id: result.slide_id.raw,
                    presentationName: result.presentation_name.raw,
                    index: parseInt(flippedSlideSips[result.slide_id.raw]),
                    getThumbnailUrl: () => new Slide({ id: result.slide_id.raw }).load().then(slide => slide.getThumbnailUrl(result.presentation_id.raw))
                };
            }).filter(x => x);

            //remove results with duplicate ids, this happens when a team slide is in multiple presentations
            results = results.filter((result, index, arr) => arr.findIndex(i => (i.id === result.id)) === index);
        }

        return results;
    }
}
